// computeLineDiff returns a unified line diff between two texts as an array of
// { type: 'add' | 'del' | 'ctx', text } rows. LCS-based, library-free.
//
// StackSets have no native CloudFormation change-set API, so this powers a
// legible "what will change" preview of the old vs new template when editing.
// For very large templates the O(n·m) LCS table is skipped in favour of a cheap
// line-set summary so the UI never hangs.
const MAX_DIFF_LINES = 1500;

export function computeLineDiff(oldText, newText) {
  const a = String(oldText ?? '').split('\n');
  const b = String(newText ?? '').split('\n');

  if (a.length > MAX_DIFF_LINES || b.length > MAX_DIFF_LINES) {
    const bSet = new Set(b);
    const aSet = new Set(a);
    const added = b.filter((l) => !aSet.has(l)).length;
    const removed = a.filter((l) => !bSet.has(l)).length;
    return { tooLarge: true, added, removed, rows: [] };
  }

  const n = a.length;
  const m = b.length;
  const dp = Array.from({ length: n + 1 }, () => new Int32Array(m + 1));
  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  const rows = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) { rows.push({ type: 'ctx', text: a[i] }); i += 1; j += 1; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { rows.push({ type: 'del', text: a[i] }); i += 1; }
    else { rows.push({ type: 'add', text: b[j] }); j += 1; }
  }
  while (i < n) { rows.push({ type: 'del', text: a[i] }); i += 1; }
  while (j < m) { rows.push({ type: 'add', text: b[j] }); j += 1; }

  const added = rows.filter((r) => r.type === 'add').length;
  const removed = rows.filter((r) => r.type === 'del').length;
  return { tooLarge: false, added, removed, rows };
}
